/**
 * useAnimationPause - Custom hook to pause animations for off-screen elements
 * Based on BurrowTV-LITE-1local template (line ~10041)
 * 
 * Reduces CPU/GPU usage by pausing animations when elements are off-screen
 * Automatically resumes when elements come back into view
 */

import { useEffect, useRef, useState } from 'react';

/**
 * Hook to pause animations when element is off-screen
 * @param {object} options - Configuration options
 * @param {string} options.rootMargin - Margin around viewport (default: '50px')
 * @param {number} options.threshold - Visibility threshold 0-1 (default: 0)
 * @param {boolean} options.enabled - Enable/disable hook (default: true)
 * @returns {object} { ref } - Ref to attach to element
 */
export function useAnimationPause(options = {}) {
  const {
    rootMargin = '50px',
    threshold = 0,
    enabled = true
  } = options;
  
  const elementRef = useRef(null);
  
  useEffect(() => {
    const element = elementRef.current;
    if (!element || !enabled) return;
    
    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      return;
    }
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Element visible - resume animations
            element.classList.remove('paused-animation');
          } else {
            // Element off-screen - pause animations
            element.classList.add('paused-animation');
          }
        });
      },
      {
        rootMargin,
        threshold
      }
    );
    
    observer.observe(element);
    
    return () => {
      if (element) {
        observer.unobserve(element);
        element.classList.remove('paused-animation');
      }
    };
  }, [rootMargin, threshold, enabled]);
  
  return { ref: elementRef };
}

/**
 * Hook to reduce motion for users with prefers-reduced-motion
 * @returns {boolean} True if user prefers reduced motion
 */
export function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e) => {
      setPrefersReducedMotion(e.matches);
    };
    
    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Fallback for older browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);
  
  return prefersReducedMotion;
}

/**
 * Hook to pause video/animations when page is not visible (user switched tabs)
 * @param {Function} onVisibilityChange - Callback with (isVisible) parameter
 */
export function usePageVisibility(onVisibilityChange) {
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === 'visible';
      if (onVisibilityChange) {
        onVisibilityChange(isVisible);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [onVisibilityChange]);
}

/**
 * Hook to conditionally enable animations based on device capability
 * @returns {boolean} True if animations should be enabled
 */
export function useShouldAnimate() {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [shouldAnimate, setShouldAnimate] = useState(true);
  
  useEffect(() => {
    // Import DeviceConfig dynamically to avoid circular dependency
    import('../utils/DeviceConfig').then(module => {
      const DeviceConfig = module.default;
      const isLowEnd = DeviceConfig.lowEndDevice;
      
      // Disable animations if:
      // 1. User prefers reduced motion, OR
      // 2. Device is low-end
      setShouldAnimate(!prefersReducedMotion && !isLowEnd);
    });
  }, [prefersReducedMotion]);
  
  return shouldAnimate;
}

export default useAnimationPause;
